// ═══════════════════════════════════════════════════════════════
// Impact — Detailed Capture
// Navigation page for Home and Infrastructure assessment forms
// ═══════════════════════════════════════════════════════════════

import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Stack, SimpleGrid, Button, ThemeIcon, Center,
} from '../../components/gab/GabComponents';
import {
  IconHome, IconBuilding, IconBolt, IconAlertTriangle, IconArrowLeft,
} from '@tabler/icons-react';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';

export default function DetailedCapture() {
  const navigate = useNavigate();
  const { activeIncident } = useAuthStore();
  const { canEdit } = useModulePermission('impact');

  if (!activeIncident) {
    return (
      <Center py={80}>
        <Stack align="center" gap="sm">
          <ThemeIcon size={60} radius="xl" variant="light" color="#939598">
            <IconAlertTriangle size={30} />
          </ThemeIcon>
          <Title order={4}>No Incident Selected</Title>
          <Text size="sm" c="dimmed">Select an active incident before starting a detailed assessment.</Text>
          <Button variant="light" onClick={() => navigate('/impact')}>
            Back to Dashboard
          </Button>
        </Stack>
      </Center>
    );
  }

  if (!canEdit) {
    return (
      <Center py={80}>
        <Stack align="center" gap="sm">
          <Text size="lg" fw={600} c="dimmed">View Only</Text>
          <Text size="sm" c="dimmed">You do not have permission to create assessments.</Text>
          <Button variant="light" onClick={() => navigate('/impact/assessments')}>
            View Assessments
          </Button>
        </Stack>
      </Center>
    );
  }

  return (
    <div>
      {/* Header */}
      <Group mb="lg">
        <Button
          variant="subtle"
          color="#939598"
          size="xs"
          leftSection={<IconArrowLeft size={14} />}
          onClick={() => navigate('/impact')}
        >
          Back
        </Button>
        <div>
          <Title order={3} c="black">Detailed Assessment</Title>
          <Text size="sm" c="dimmed">
            {activeIncident.name} — Choose an assessment type
          </Text>
        </div>
      </Group>

      {/* Assessment Type Cards */}
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <Paper
          p="lg"
          radius="sm"
          withBorder
          style={{ cursor: 'pointer', borderLeft: '4px solid #16a34a' }}
          onClick={() => navigate('/impact/home')}
        >
          <Group wrap="nowrap">
            <ThemeIcon size={48} radius="md" variant="light" color="var(--color-success)">
              <IconHome size={24} />
            </ThemeIcon>
            <div>
              <Text fw={600} size="lg">Home Assessment</Text>
              <Text size="sm" c="dimmed">
                Detailed residential damage assessment. Captures dwelling type, occupancy,
                habitability, insurance status, and damage details for individual homes.
              </Text>
            </div>
          </Group>
        </Paper>

        <Paper
          p="lg"
          radius="sm"
          withBorder
          style={{ cursor: 'pointer', borderLeft: '4px solid #0ea5e9' }}
          onClick={() => navigate('/impact/infra')}
        >
          <Group wrap="nowrap">
            <ThemeIcon size={48} radius="md" variant="light" color="var(--color-primary-700)">
              <IconBuilding size={24} />
            </ThemeIcon>
            <div>
              <Text fw={600} size="lg">Infrastructure Assessment</Text>
              <Text size="sm" c="dimmed">
                Detailed public infrastructure and facility damage assessment. Captures
                facility type, estimated repair costs, and PA category for FEMA reporting.
              </Text>
            </div>
          </Group>
        </Paper>
      </SimpleGrid>

      {/* Quick Capture Link */}
      <Paper p="md" radius="sm" withBorder mt="lg">
        <Group justify="space-between">
          <div>
            <Text fw={500}>Need something faster?</Text>
            <Text size="sm" c="dimmed">
              Quick Capture lets you log a damage assessment in under 30 seconds.
            </Text>
          </div>
          <Button
            variant="light"
            leftSection={<IconBolt size={14} />}
            onClick={() => navigate('/impact/capture')}
          >
            Quick Capture
          </Button>
        </Group>
      </Paper>
    </div>
  );
}
